import React, { useCallback } from 'react';
import { Grid } from '~/components/';
import { useForm } from '~/hooks';
import { FaPencilAlt, FaTrash} from 'react-icons/fa';
import LienItem from './LienItem';
import { MdAddCircle } from 'react-icons/md';
import { useDeleteSectionDetail, useSaveSectionDetail } from '../../hooks';
import sectionTypes from '../sectionTypes';

/**
 * @typedef LienGridValues
 * @property {number}   sectionId
 * @property {string}   titleOpinionId 
 * @property {boolean}  addRecordedInBook
 * @property {[object]} sectionDetails
 * @property {boolean}  addRecordBookFlag
 * 
 * @typedef FormState
 * @property {LienGridValues} values The current form values
 */

/** @type {LienGridValues} */

const defaultState = {
    id: 0,
    sectionDetails: [{
        sectionDetailId: 0,
        type: 'liens',
        lienType: '',
        otherLienType: '',
        action: '',
        trustee: '',
        inFavorOf: '',
        dated: '',
        recordDate: '',
        book: '',
        pageNo: '',
        countyRegistry: '',
        instrumentNo: '',
        amount: ''
    }],    
    addLienFlag: false,
    liensDetailData: {},
    editRecord: false,
    titleOpinionId: 0,
    sectionType: sectionTypes.LIENS,
    editMode: true
};

const loadData = async (values) => {
    return {
        ...values,
    };
};

const LienGrid = (props) => {

    const onLoad = useCallback((values) => {
        return loadData(values);
    }, [props.sectionId]);

    const { state, fieldActions } = useForm({
        initialState: { 
            ...defaultState},
        onLoad,     
        props,       
        debug: true 

    }); 

    const lienDetail =  props.sectionDetails ? props.sectionDetails.map((detail) => {
        return {
            ...detail,
            id: detail.sectionDetailId,
            lienType: detail._data.lienType.value,
            otherLienType: detail._data.otherLienType,
            action: detail._data.action.value,
            trustee: detail._data.trustee,
            inFavorOf: detail._data.inFavorOf,
            dated: detail._data.dated,
            recordDate: detail._data.recordDate,
            book: detail._data.book,
            pageNo: detail._data.pageNo,
            countyRegistry: detail._data.countyRegistry,
            instrumentNo: detail._data.instrumentNo,
            amount: detail._data.amount            
        } ;
    }) : [];

    const { deleteSectionDetail } = useDeleteSectionDetail(state.values.data, state.values.sectionDetailId);

    const onEditClick = async (data) => {
        await fieldActions.onChange(data, 'liensDetailData');
        await fieldActions.onChange(true, 'editRecord');
        fieldActions.onChange(true, 'addLienFlag');
    };

    const onDeleteClick = (id) => {
        deleteSectionDetail(id);
    };

    const addLienModal = () => {
        fieldActions.onChange(false, 'editRecord');
        fieldActions.onChange(true, 'addLienFlag');
    };

    const cancelAddLien = () => {
        fieldActions.onChange(false, 'addLienFlag');
    }; 

    const { saveField } = useSaveSectionDetail(state.values.data, state.values.sectionType, props.initialDefaultData, props.titleOpinionId, state.values.sectionDetailId);
    
    const addLien = async (values) => { 
        const data = {
            sectionId: props.sectionId,
            type: 'lien',
            lienType: values.lienType,
            otherLienType: values.otherLienType,
            action: values.action,
            trustee: values.trustee,
            inFavorOf: values.inFavorOf,
            dated: values.dated,
            recordDate: values.recordDate,
            book: values.book,
            pageNo: values.pageNo,
            countyRegistry: values.countyRegistry,
            instrumentNo: values.instrumentNo,
            amount: values.amount       
        };
        await fieldActions.onChange(data, 'data');
        await saveField( state.values.data, state.values.sectionType, props.initialDefaultData, props.titleOpinionId, state.values.sectionDetailId);
        await fieldActions.onChange(false, 'addLienFlag');
        
    };

    const saveLien = async (newData) => {
        const data = {
            sectionId: props.sectionId,
            sectionDetailId: newData.liensDetailData.sectionDetailId,
            type: 'lien',
            lienType: newData.lienType,
            otherLienType: newData.otherLienType,
            action: newData.action,
            trustee: newData.trustee,
            inFavorOf: newData.inFavorOf,
            dated: newData.dated,
            recordDate: newData.recordDate,
            book: newData.book,
            pageNo: newData.pageNo,
            countyRegistry: newData.countyRegistry,
            instrumentNo: newData.instrumentNo,
            amount: newData.amount              
        };
        
        await fieldActions.onChange(data, 'data');
        await saveField( state.values.data, state.values.sectionType, props.initialDefaultData, props.titleOpinionId, data.sectionDetailId);
        fieldActions.onChange(false, 'addLienFlag');
    };

    const columns = [
        {
            title: 'Lien Type',
            field: 'lienType',
            cell: ({ data }) => data.lienType === 'Other' ? data.otherLienType : data.lienType
        },
        {
            title: 'Action',
            field: 'action',            
        },
        {
            title: 'Trustee',
            field: 'trustee',
        },
        {
            title: 'In Favor Of',
            field: 'inFavorOf',
            
        },
        {
            title: 'Dated',
            field: 'dated',
            cell: ({ data }) => {
                return (
                    <p>
                        {data.dated ? new Date(data.dated).toLocaleDateString()  : null}
                    </p>
                );
            }
        },
        {
            title: 'Record Date',
            field: 'recordDate',
            cell: ({ data }) => {
                return (
                    <p>
                        {data.recordDate ? new Date(data.recordDate).toLocaleDateString() : null}
                    </p>
                );
            }
            
        },
        {
            title: 'Book',
            field: 'book',
        },
        {
            title: 'Page No.',
            field: 'pageNo',
            
        },
        {
            title: 'County Registry',
            field: 'countyRegistry',
        },
        {
            title: 'Instrument No.',
            field: 'instrumentNo',
        },
        {
            title: 'Amount',
            field: 'amount',
            
        },
        {
            field: 'edit',
            canSort: false,
            cell: ({ data }) => {
                return (
                    <>
                        {!props.editMode ? '' :
                            <button className="btn btn-link rightSpace ml-5" 
                                onClick={() => onEditClick(data)}>
                                <FaPencilAlt />
                            </button>
                        }
                        
                    </>
                );
            }
        },
        {
            field: 'delete',
            canSort: false,
            cell: ({ data }) => {
                return (
                    <>
                        {!props.editMode ? '' :                    
                            <button className="btn btn-link rightSpace ml-5" 
                                onClick={() => onDeleteClick(data.id)}>
                                <FaTrash />
                            </button>
                        }
                    </>
                );
            }
        }
    ];

    return (
        <React.Fragment>
            <div className="col-span-12 mt-5">
                <Grid
                    className="grid-stretch"
                    columns={columns}
                    data={lienDetail}
                />
                <LienItem
                    isModalOpen={state.values.addLienFlag}
                    liensDetailData={state.values.liensDetailData}
                    editRecord={state.values.editRecord}
                    onCancel={() => cancelAddLien()}
                    headerTxt={'Add Lien'}
                    sectionId={props.sectionId}
                    addLien={(values) => addLien(values)}
                    saveLien={(b) => saveLien(b)}
                />                
            </div>
            {!props.editMode ? '' : 
                <div className='mt-auto col-span-12'>
                    <div className="flex justify-start items-center mt-auto">
                        <button
                            className='py-2 px-2 rounded-full text-white flex items-center focus:outline-none bg-blue-60 disabled:opacity-50'
                            onClick={() => addLienModal()}
                        >
                            <MdAddCircle className="mr-2" size="1.25rem" />
                            Add Lien
                        </button>
                    </div>
                </div>
            
            }
            
        </React.Fragment>
    );
};

export default LienGrid;